import {
  Avatar,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ClearIcon from '@mui/icons-material/Clear';
import { Skeleton } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import UserService, {
  SearchTypes,
} from '../../../../shared-services/User/UserService';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import { IRetentionStatus } from '../../../../shared-interfaces/User/IRetentionStatus';
import { IUserRetentionStatus } from '../../../../shared-interfaces/User/IUserRetentionStatus';
import { Role, RoleToString } from '../../../../shared-types/Roles';
import { UserManagementState } from '../UserManagement';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';

interface ISelectedUserList {
  actionType?: 'retention' | 'role';
  users?: ICoreUser[];
  userRetentionStatuses?: IUserRetentionStatus[];
  onClear?: (user: ICoreUser) => void;
}

const useStyles = makeStyles({
  row: {
    padding: '4px 4px 2px 16px',
  },
  avatar: {
    height: '30px',
    width: '30px',
    margin: '4px',
  },
  primaryTextValid: {
    fontSize: '0.875rem',
    lineHeight: '1.45',
  },
  secondaryTextValid: {
    fontSize: '0.875rem',
    lineHeight: '1.45',
  },
});

const SelectedUserList = (props: ISelectedUserList) => {
  const classes = useStyles();

  const state = useContext(UserManagementState);
  const userAttributeContext = useContext(UserAttributesContext);

  const [isLoading, setIsLoading] = useState(state.selectAll);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [loadedUsers, setLoadedUsers] = useState<ICoreUser[]>([]);

  const getUsers = useCallback(() => {
    if (!hasMore) return Promise.resolve();
    setIsLoading(true);
    return (async () => {
      const users = await UserService.search({
        term: state.filter.search,
        RoleIds: state.filter.roles,
        RetentionStatusIds: state.filter.retentionStatus,
        take: 50,
        skip: loadedUsers.length,
        inNetwork: false,
        enrolledOnly: false,
        SearchType: SearchTypes.like,
      });
      setLoadedUsers([...loadedUsers, ...users.items] as ICoreUser[]);

      if (users.items.length < 50) setHasMore(false);
      setIsLoading(false);
    })();
  }, [loadedUsers, hasMore]);

  useEffect(() => {
    if (state.selectAll) getUsers().finally(() => setIsInitialLoad(false));
  }, []);

  const generateSecondaryText = (
    role: Role,
    userRetentionStatus?: IRetentionStatus,
  ) => {
    if (props.actionType === 'retention') {
      return `${userRetentionStatus?.name ?? 'No Retention Status'}`;
    } else if (props.actionType === 'role') {
      return RoleToString(role);
    }
    return '';
  };

  const SelectedUserRow = (rowProps: { user: ICoreUser }) => {
    return (
      <Grid container direction="row" className={classes.row}>
        <Grid item xs={1}>
          <Avatar
            variant="circular"
            className={classes.avatar}
            alt={`${
              rowProps.user.preferredFirstName ?? rowProps.user.firstName
            } ${rowProps.user.lastName}`}
            src={
              rowProps.user.profileImageFilePath ??
              rowProps.user.institutionProfileImageFilePath
            }
          />
        </Grid>
        <Grid item xs={10}>
          {/* Name/Status Grid START */}
          <Grid container direction="column">
            <Grid item>
              <Typography>
                {(rowProps.user.preferredFirstName ?? rowProps.user.firstName) +
                  ' ' +
                  rowProps.user.lastName}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">
                {generateSecondaryText(
                  rowProps.user.role,
                  userAttributeContext.retentionStatuses.find(
                    r => r.id === rowProps.user.retentionStatusId,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          {/* Name/Status Grid END */}
        </Grid>
        <Grid item xs={1}>
          {((props.users && props.users?.length > 1 && props.onClear) ||
            (!props.users && props.onClear)) && (
            <IconButton
              edge="end"
              aria-label="Clear"
              onClick={async () =>
                props.onClear ? props.onClear(rowProps.user) : {}
              }
              size="large"
            >
              <ClearIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    );
  };

  const SelectedUserRowLoadingContainer = () => {
    return (
      <Grid container direction="row" className={classes.row}>
        <Grid item xs={1}>
          <Skeleton variant="circular" className={classes.avatar} />
        </Grid>
        <Grid item xs={10}>
          {/* Name/Status Grid START */}
          <Grid container direction="column">
            <Grid item>
              <Skeleton
                variant="text"
                width={200}
                className={classes.primaryTextValid}
              />
            </Grid>
            <Grid item>
              <Skeleton
                variant="text"
                width={150}
                className={classes.secondaryTextValid}
              />
            </Grid>
          </Grid>
          {/* Name/Status Grid END */}
        </Grid>
        <Grid item xs={1}></Grid>
      </Grid>
    );
  };

  return (
    <>
      {state.selectAll && !props.users ? (
        !isInitialLoad ? (
          <>
            <InfiniteScroll
              loadMore={() => {
                return getUsers();
              }}
              initialLoad={false}
              hasMore={!isLoading}
              useWindow={false}
            >
              {props.actionType === 'retention'
                ? loadedUsers
                    .filter(x => {
                      return x.role <= Role.PeerAdvisor;
                    })
                    .map(
                      (user, i) =>
                        state.excludeUsers.findIndex(x => x.id === user.id) ===
                          -1 && <SelectedUserRow key={i} user={user} />,
                    )
                : loadedUsers.map(
                    (user, i) =>
                      state.excludeUsers.findIndex(x => x.id === user.id) ===
                        -1 && <SelectedUserRow key={i} user={user} />,
                  )}
            </InfiniteScroll>

            {isLoading && loadedUsers.length > 0 && hasMore && (
              <div className="loading-bar-container">
                <LinearProgress />
              </div>
            )}
          </>
        ) : (
          <>
            <Box>
              {[...Array(50)].map((e, i) => (
                <SelectedUserRowLoadingContainer key={i} />
              ))}
            </Box>
          </>
        )
      ) : props.actionType === 'retention' ? (
        (props.users ? props.users : state.selectedUsers)
          .filter(x => {
            return x.role <= Role.PeerAdvisor;
          })
          .map((user, i) => <SelectedUserRow key={i} user={user} />)
      ) : (
        (props.users ? props.users : state.selectedUsers).map((user, i) => (
          <SelectedUserRow key={i} user={user} />
        ))
      )}
    </>
  );
};

export default SelectedUserList;
